import { getCookie } from 'utils/CookieUtil';
import { isEmpty } from 'utils/ObjectUtil';

const MATOMO_CUSTOM_DIMENTIONS = {
    gdprConsent: 1,
    noAdBlocker: 2,
    currency: 3,
    timeZone: 4
};

// Reference: utag.gdpr.preferences_prompt.categories
const consentType = {
    c1: 'analytics',
    c2: 'affiliates',
    c3: 'display_ads',
    c4: 'search',
    c5: 'email',
    c6: 'personalization',
    c7: 'social',
    c8: 'big_data',
    c9: 'misc',
    c10: 'cookiematch',
    c11: 'cdp',
    c12: 'mobile',
    c13: 'engagement',
    c14: 'monitoring',
    c15: 'crm'
};

const CONSENT_COUNT = Object.keys(consentType).length;

const consentStatus = {
    fullConsent: 'Full Consent',
    declinedConsent: 'Declined Consent',
    undecidedConsent: 'Undecided Consent'
};

/**
 * Matomo Initialization
 * Reference: https://developer.matomo.org/guides/tracking-javascript-guide
 *   1. Initializing the _paq array for queuing tracking commands.
 *   2. Queuing commands to track the current page view and enable link tracking.
 *   3. Setting up the tracking URL and site ID.
 *   4. Dynamically creating and inserting a script element to load the Matomo tracking script.
 */
export const initializeMatomo = () => {
    /**
     * Initializes the _paq array if it does not already exist.
     * The _paq array is used to queue tracking commands before the Matomo tracking script is fully loaded.
     */
    const _paq = (window._paq = window._paq || []);
    _paq.push(['enableLinkTracking']); // enables automatic tracking of clicks on links to other pages within the site.

    const MATOMO_BASE_URL = '//matomo.garmin.com'; // from utag.1451.js, u.data.tracker_url
    const IDSITE = '6'; // from utag.1451.js, u.data.site_id

    _paq.push(['setTrackerUrl', `${MATOMO_BASE_URL}/matomo.php`]); // sets the URL where the tracking data should be sent.
    _paq.push(['setSiteId', IDSITE]); // sets the site ID to uniquely identify the website being tracked
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `${MATOMO_BASE_URL}/matomo.js`;
    document.head.appendChild(script);

    // initialize custom dimentions
    setCustomDimensions();
};

/**
 * Push an event to Matomo
 * @param {string} event
 * @param {string[]} [values]
 */
export const pushEventToMatomo = (event, values = []) => {
    const _paq = (window._paq = window._paq || []);
    _paq.push([event, ...values]);
};

/**
 * Set Matomo custom dimensions for gdpr consents, no ad blocker, currency and timezone
 *
 * Tealium Reference: utag.js - getConsentState function in utag.gdpr object
 */
export const setCustomDimensions = () => {
    /** Step 1. Get consent preferences from cookie */
    const consentCookieValues = {};
    const cookieKey = 'CONSENTMGR';
    const cookieValues = getCookie(cookieKey) ?? '';
    const consentKeys = Object.keys(consentType);
    cookieValues.split('|').forEach((i) => {
        const [key, value] = i.split(':'); // e.g. ['c1', '1'], ..., ['c15', '1'], ['ts', '1721025131275'], ['consent', 'true']
        // filter out keys that are not c1 - c15
        if (consentKeys.includes(key)) {
            consentCookieValues[key] = value;
        }
    });

    /**
     * Step 2. Get consent status string based on cookie values
     * Tealium Reference: utag.js.1451.js - get_user_consent_state()
     */
    let consentDescription = "Couldn't Parse Consent";
    if (isEmpty(consentCookieValues)) {
        // Case 1. User hasn't yet initialized consent preferences from TrustArc widget
        consentDescription = consentStatus.undecidedConsent;
    } else {
        const acceptedConsent = [];
        for (const [key, value] of Object.entries(consentCookieValues)) {
            if (value === '1') {
                acceptedConsent.push(consentType[key]);
            }
        }

        if (acceptedConsent.length === CONSENT_COUNT) {
            // Case 2. User accepts all consents
            consentDescription = consentStatus.fullConsent;
        } else if (acceptedConsent.length === 0) {
            // Case 3. User rejects all consents
            consentDescription = consentStatus.declinedConsent;
        } else {
            // Case 4. User accepts partial consents
            consentDescription = `Partial Consent: ${acceptedConsent.join(',')}`;
        }
    }

    /** Step 3. Setup Custom Dimensions */
    // GDPR Consent (1)
    pushCustomDimensions(MATOMO_CUSTOM_DIMENTIONS.gdprConsent, consentDescription); // this needs to be updated whenever user updated the consent preference
    // No Ad Blocker (2)
    pushCustomDimensions(MATOMO_CUSTOM_DIMENTIONS.noAdBlocker, 'true');
    // Currency (3)
    pushCustomDimensions(MATOMO_CUSTOM_DIMENTIONS.currency, 'None');
    // Timezone (4)
    pushCustomDimensions(MATOMO_CUSTOM_DIMENTIONS.timeZone, Intl.DateTimeFormat().resolvedOptions().timeZone);
};

/**
 * Update specific custom dymension
 * @param {number} customDimensionId Custom Dimension ID
 * @param {string} customDimensionValue Custom Dimension Value
 */
const pushCustomDimensions = (customDimensionId, customDimensionValue) => {
    _paq.push(['setCustomDimension', customDimensionId, customDimensionValue]);
};
